import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GripVertical, Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import {
  buildNoteFolderTree,
  createNote,
  createNoteFolder,
  deleteNoteFolder,
  listNoteFolders,
  listNotes,
  moveNote,
  moveNoteFolder,
  noteDisplayTitle,
  renameNoteFolder,
  reorderNote,
  type Note,
  type NoteFolder,
} from "@/lib/composerNotes";
import { NotesFolderTreeArchive, type NoteSelection } from "./NotesFolderTreeArchive";
import { NOTE_DRAG_MIME } from "./NotesFolderTree";

import { NoteEditorSheet } from "./NoteEditorSheet";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Props = {
  workspaceId: string;
};

const QK = {
  folders: (ws: string) => ["composer-notes-folders", ws] as const,
  notes: (ws: string) => ["composer-notes", ws] as const,
};

type DropPos = { id: string; where: "before" | "after" } | null;

export const NotesPanelArchive = ({ workspaceId }: Props) => {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { resolved } = useTheme();
  const [selection, setSelection] = useState<NoteSelection>({ kind: "root" });
  const [search, setSearch] = useState("");
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [renameTarget, setRenameTarget] = useState<NoteFolder | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [newFolderParent, setNewFolderParent] = useState<{ folder: NoteFolder | null } | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<NoteFolder | null>(null);
  const [dropPos, setDropPos] = useState<DropPos>(null);

  const foldersQ = useQuery({
    queryKey: QK.folders(workspaceId),
    queryFn: () => listNoteFolders(workspaceId),
    enabled: !!workspaceId,
  });
  const notesQ = useQuery({
    queryKey: QK.notes(workspaceId),
    queryFn: () => listNotes(workspaceId),
    enabled: !!workspaceId,
  });

  const folders = useMemo(() => foldersQ.data ?? [], [foldersQ.data]);
  const notes = useMemo(() => notesQ.data ?? [], [notesQ.data]);

  const tree = useMemo(() => buildNoteFolderTree(folders, notes), [folders, notes]);
  const rootNoteCount = useMemo(() => notes.filter((n) => !n.folder_id).length, [notes]);


  const invalidate = () => {
    qc.invalidateQueries({ queryKey: QK.folders(workspaceId) });
    qc.invalidateQueries({ queryKey: QK.notes(workspaceId) });
  };

  const createNoteMut = useMutation({
    mutationFn: () =>
      createNote({
        workspaceId,
        folderId: selection.kind === "folder" ? selection.folderId : null,
      }),
    onSuccess: (note) => {
      invalidate();
      setEditingNote(note);
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't create note", description: err.message, variant: "destructive" }),
  });

  const moveNoteMut = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) => moveNote(id, folderId),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't move note", description: err.message, variant: "destructive" }),
  });

  const reorderNoteMut = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) => reorderNote(id, sortOrder),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't reorder note", description: err.message, variant: "destructive" }),
  });

  const moveFolderMut = useMutation({
    mutationFn: ({ id, newParentId }: { id: string; newParentId: string | null }) =>
      moveNoteFolder({ id, newParentId }),
    onSuccess: () => invalidate(),
    onError: (err: Error) =>
      toast({ title: "Couldn't move folder", description: err.message, variant: "destructive" }),
  });

  const deleteFolderMut = useMutation({
    mutationFn: (id: string) => deleteNoteFolder(id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't delete folder", description: err.message, variant: "destructive" }),
  });

  const renameFolderMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameNoteFolder(id, name),
    onSuccess: () => {
      invalidate();
      setRenameTarget(null);
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't rename", description: err.message, variant: "destructive" }),
  });

  const createFolderMut = useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: string | null }) =>
      createNoteFolder({ workspaceId, name, parentFolderId: parentId }),
    onSuccess: () => {
      invalidate();
      setNewFolderParent(null);
      setNewFolderName("");
    },
    onError: (err: Error) =>
      toast({ title: "Couldn't create folder", description: err.message, variant: "destructive" }),
  });

  const filteredNotes = useMemo(() => {
    let list = notes;
    if (selection.kind === "folder") {
      list = list.filter((n) => n.folder_id === selection.folderId);
    } else {
      // root: notes that live in the permanent Notes folder
      list = list.filter((n) => !n.folder_id);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((n) => {
        return (
          (n.title ?? "").toLowerCase().includes(q) ||
          (n.body ?? "").toLowerCase().includes(q)
        );
      });
    }
    // Sort by sort_order ASC; within same bucket, most-recently-updated first.
    return [...list].sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.updated_at < b.updated_at ? 1 : -1;
    });
  }, [notes, selection, search]);

  const addButtonClass = cn(
    "h-9 w-9 rounded-md inline-flex items-center justify-center transition-colors",
    resolved === "dark"
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : "bg-blue-600 text-white hover:bg-blue-700",
  );

  const handleNoteDrop = (targetNote: Note, where: "before" | "after", draggedId: string) => {
    if (draggedId === targetNote.id) return;
    const idx = filteredNotes.findIndex((n) => n.id === targetNote.id);
    if (idx < 0) return;
    const prevIdx = where === "before" ? idx - 1 : idx;
    const nextIdx = where === "before" ? idx : idx + 1;
    const prev = filteredNotes[prevIdx];
    const next = filteredNotes[nextIdx];
    let newSort: number;
    if (prev && next) {
      newSort = (prev.sort_order + next.sort_order) / 2;
    } else if (prev) {
      newSort = prev.sort_order + 1000;
    } else if (next) {
      newSort = next.sort_order - 1000;
    } else {
      newSort = Date.now();
    }
    reorderNoteMut.mutate({ id: draggedId, sortOrder: newSort });
  };

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search notes"
            className="h-9 pl-7 text-sm"
          />
        </div>
        <button
          type="button"
          aria-label="New note"
          className={addButtonClass}
          onClick={() => createNoteMut.mutate()}
          disabled={createNoteMut.isPending}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId={`composer.archive.notes.folders-size.${workspaceId}`}
        className="min-h-[140px] flex-1"
      >
        <ResizablePanel defaultSize={28} minSize={18} maxSize={55}>
          <div className="h-full rounded-md border border-border bg-card p-1.5 overflow-auto">
            <NotesFolderTreeArchive
              tree={tree}
              rootNoteCount={rootNoteCount}
              selection={selection}
              onSelect={setSelection}
              onCreateFolder={(parent) => {
                setNewFolderName("");
                setNewFolderParent({ folder: parent });
              }}
              onRenameFolder={(f) => {
                setRenameValue(f.name);
                setRenameTarget(f);
              }}
              onDeleteFolder={(f) => setDeleteTarget(f)}
              onMoveNote={(id, folderId) => moveNoteMut.mutate({ id, folderId })}
              onMoveFolder={(id, newParentId) => moveFolderMut.mutate({ id, newParentId })}
            />
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle className="mx-1 bg-transparent" />
        <ResizablePanel defaultSize={72} minSize={45}>
          <div className="h-full space-y-0.5 rounded-md border border-border bg-card p-1.5 overflow-auto">
            {filteredNotes.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                {search ? "No notes match your search." : "No notes here yet."}
              </p>
            ) : (
              filteredNotes.map((n) => {
                const indicator =
                  dropPos && dropPos.id === n.id ? dropPos.where : null;
                return (
                  <div key={n.id} className="relative">
                    {indicator === "before" && (
                      <div className="pointer-events-none absolute left-1 right-1 -top-px h-0.5 rounded bg-primary" />
                    )}
                    <div
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(NOTE_DRAG_MIME, JSON.stringify({ id: n.id }));
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragOver={(e) => {
                        if (!e.dataTransfer.types.includes(NOTE_DRAG_MIME)) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        const rect = e.currentTarget.getBoundingClientRect();
                        const where: "before" | "after" =
                          e.clientY < rect.top + rect.height / 2 ? "before" : "after";
                        setDropPos({ id: n.id, where });
                      }}
                      onDragLeave={() => setDropPos(null)}
                      onDrop={(e) => {
                        const payload = e.dataTransfer.getData(NOTE_DRAG_MIME);
                        const pos = dropPos;
                        setDropPos(null);
                        if (!payload) return;
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          const { id } = JSON.parse(payload) as { id: string };
                          if (!id || !pos) return;
                          handleNoteDrop(n, pos.where, id);
                        } catch { /* noop */ }
                      }}
                      onClick={() => setEditingNote(n)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setEditingNote(n);
                        }
                      }}
                      className="group flex w-full cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted/60"
                    >
                      <GripVertical
                        className="h-3.5 w-3.5 shrink-0 cursor-grab text-muted-foreground opacity-40 group-hover:opacity-100"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{noteDisplayTitle(n)}</div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {new Date(n.updated_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    {indicator === "after" && (
                      <div className="pointer-events-none absolute left-1 right-1 -bottom-px h-0.5 rounded bg-primary" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <NoteEditorSheet
        note={editingNote}
        open={editingNote !== null}
        onOpenChange={(o) => {
          if (!o) setEditingNote(null);
        }}
        onChanged={invalidate}
      />

      {/* Rename folder dialog */}
      <Dialog open={renameTarget !== null} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
            <DialogDescription>Give this folder a new name.</DialogDescription>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && renameTarget) {
                renameFolderMut.mutate({ id: renameTarget.id, name: renameValue });
              }
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button
              onClick={() => renameTarget && renameFolderMut.mutate({ id: renameTarget.id, name: renameValue })}
              disabled={!renameValue.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New folder dialog */}
      <Dialog open={newFolderParent !== null} onOpenChange={(o) => !o && setNewFolderParent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newFolderParent?.folder ? `New sub-folder in "${newFolderParent.folder.name}"` : "New folder"}
            </DialogTitle>
            <DialogDescription>Organize your notes into folders.</DialogDescription>
          </DialogHeader>
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            autoFocus
            placeholder="Folder name"
            onKeyDown={(e) => {
              if (e.key === "Enter" && newFolderName.trim()) {
                createFolderMut.mutate({ name: newFolderName, parentId: newFolderParent?.folder?.id ?? null });
              }
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewFolderParent(null)}>Cancel</Button>
            <Button
              onClick={() =>
                createFolderMut.mutate({ name: newFolderName, parentId: newFolderParent?.folder?.id ?? null })
              }
              disabled={!newFolderName.trim()}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete folder confirm */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this folder?</AlertDialogTitle>
            <AlertDialogDescription>
              Notes inside <span className="font-medium">{deleteTarget?.name}</span> will move to
              Notes. Sub-folders will be deleted (their notes also move to Notes).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteFolderMut.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
